// Blog service for managing blog posts with Neon backend
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Function to get a single batch of blog posts (full content).
// Returns { posts, total, ok } so callers can paginate without hitting the 6MB
// limit. `ok` is false when the request failed (e.g. 413 payload too large),
// which lets callers retry with a smaller batch size instead of giving up.
export const getPostsBatch = async (offset = 0, limit = 2) => {
  try {
    const response = await fetch(`${API_URL}/posts?offset=${offset}&limit=${limit}`);

    if (!response.ok) {
      console.error(`Error fetching posts batch (status ${response.status})`);
      return { posts: [], total: null, ok: false };
    }

    const data = await response.json();

    // Backward compatibility: handle plain-array responses
    if (Array.isArray(data)) {
      return { posts: data, total: data.length, ok: true };
    }
    return { posts: data.posts || [], total: data.total ?? 0, ok: true };
  } catch (error) {
    console.error('Exception fetching blog posts:', error);
    return { posts: [], total: null, ok: false };
  }
};

// Function to get ALL blog posts by fetching every batch sequentially.
// Each network request stays small so the serverless function never exceeds
// its 6MB response limit, while still returning every article with full
// content. If a batch is still too large (e.g. posts with big base64 images),
// it automatically retries that offset one post at a time. Optionally pass an
// onBatch callback to render posts progressively as they load.
export const getAllPosts = async (onBatch = null, batchSize = 10) => {
  try {
    console.log('Fetching all blog posts from backend (batched)...');

    let offset = 0;
    let total = Infinity;
    const allPosts = [];

    while (offset < total) {
      const remaining = total === Infinity ? batchSize : total - offset;
      let limit = Math.min(batchSize, remaining);

      let { posts, total: batchTotal, ok } = await getPostsBatch(offset, limit);

      // If the batch failed (likely too large) retry one post at a time.
      if (!ok && limit > 1) {
        ({ posts, total: batchTotal, ok } = await getPostsBatch(offset, 1));
        limit = 1;
      }

      if (!ok) {
        // Skip the problematic post so the rest of the feed still loads.
        offset += 1;
        continue;
      }

      if (typeof batchTotal === 'number') total = batchTotal;
      if (!posts || posts.length === 0) break;

      allPosts.push(...posts);
      if (typeof onBatch === 'function') {
        onBatch([...allPosts]);
      }

      offset += posts.length;
    }

    console.log(`Retrieved ${allPosts.length} blog posts`);
    return allPosts;
  } catch (error) {
    console.error('Exception fetching blog posts:', error);
    return [];
  }
};

// Function to get a single blog post by slug
export const getPostBySlug = async (slug) => {
  try {
    console.log(`Fetching blog post with slug: ${slug}`);
    
    const response = await fetch(`${API_URL}/posts/${slug}`);
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Error fetching blog post:', data.error);
      return null;
    }
    
    console.log('Blog post retrieved successfully');
    return data;
  } catch (error) {
    console.error('Exception fetching blog post:', error);
    return null;
  }
};

// Function to get a single blog post by ID
export const getPostById = async (id) => {
  try {
    console.log(`Fetching blog post with id: ${id}`);
    
    const response = await fetch(`${API_URL}/posts/id/${id}`);
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Error fetching blog post:', data.error);
      return null;
    }
    
    console.log('Blog post retrieved successfully');
    return data;
  } catch (error) {
    console.error('Exception fetching blog post:', error);
    return null;
  }
};

// Function to get posts by category
export const getPostsByCategory = async (category) => {
  try {
    console.log(`Fetching blog posts in category: ${category}`);
    
    const response = await fetch(`${API_URL}/posts/category/${category}`);
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Error fetching blog posts by category:', data.error);
      return [];
    }
    
    console.log(`Retrieved ${data.length} blog posts in category ${category}`);
    return data;
  } catch (error) {
    console.error('Exception fetching blog posts by category:', error);
    return [];
  }
};

// Function to create a new blog post
export const createPost = async (post) => {
  try {
    console.log('Creating new blog post...');
    
    const response = await fetch(`${API_URL}/posts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(post)
    });
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Error creating blog post:', data.error);
      return null;
    }
    
    console.log('Blog post created successfully');
    return data;
  } catch (error) {
    console.error('Exception creating blog post:', error);
    return null;
  }
};

// Function to update a blog post
export const updatePost = async (id, updates) => {
  try {
    console.log(`Updating blog post with id: ${id}`);
    console.log('Update data:', updates);
    
    const response = await fetch(`${API_URL}/posts/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    
    console.log('Response status:', response.status);
    const data = await response.json();
    console.log('Response data:', data);
    
    if (!response.ok) {
      console.error('Error updating blog post:', data.error);
      return null;
    }
    
    console.log('Blog post updated successfully');
    return data;
  } catch (error) {
    console.error('Exception updating blog post:', error);
    return null;
  }
};

// Function to delete a blog post
export const deletePost = async (id) => {
  try {
    console.log(`Deleting blog post with id: ${id}`);
    
    const response = await fetch(`${API_URL}/posts/${id}`, {
      method: 'DELETE'
    });
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Error deleting blog post:', data.error);
      return false;
    }
    
    console.log('Blog post deleted successfully');
    return true;
  } catch (error) {
    console.error('Exception deleting blog post:', error);
    return false;
  }
};

// Function to get site settings (key/value object), e.g. { logo_url: '...' }
export const getSettings = async () => {
  try {
    const response = await fetch(`${API_URL}/settings`);
    if (!response.ok) return {};
    return await response.json();
  } catch (error) {
    console.error('Exception fetching settings:', error);
    return {};
  }
};

// Function to update one or more site settings
export const updateSettings = async (settings) => {
  try {
    const response = await fetch(`${API_URL}/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings)
    });
    return response.ok;
  } catch (error) {
    console.error('Exception updating settings:', error);
    return false;
  }
};

// Function to initialize sample blog posts
export const initializeSamplePosts = async () => {
  try {
    console.log('Checking for existing blog posts...');
    
    const response = await fetch(`${API_URL}/posts/check`);
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Cannot check for existing posts:', data.error);
      return;
    }
    
    if (data.exists) {
      console.log('Blog posts already exist, skipping initialization');
      return;
    }
    
    console.log('Creating sample blog posts...');
    
    const samplePosts = [
      {
        title: 'A Spanish-Inspired Countryside Haven: The Radiant Transformation of a Heritage Home',
        slug: 'spanish-inspired-countryside-haven',
        excerpt: 'Step into a world where heritage meets Spanish flair in this countryside haven, a radiant revamp of a historic home.',
        content: '<p>This stunning transformation brings together the best of traditional architecture and modern Spanish design elements. The project showcases how heritage properties can be thoughtfully updated while maintaining their historic character.</p><p>The renovation focused on opening up interior spaces, introducing natural light, and incorporating warm, earthy tones that reflect the Spanish countryside aesthetic.</p>',
        category: 'Architecture',
        featured_image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&h=800&fit=crop',
        author: 'Editorial Team',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        title: 'Old to Gold: Transforming an Outdated 70s Brick Dwelling into an Urban Oasis',
        slug: 'old-to-gold-70s-transformation',
        excerpt: 'Witness the remarkable journey of a 1970s brick house as it evolves into a contemporary urban sanctuary.',
        content: '<p>This remarkable renovation project demonstrates the potential hidden within dated architecture. What was once a typical 70s brick dwelling has been transformed into a stunning modern home that seamlessly blends with its urban environment.</p><p>The design team focused on sustainability, natural materials, and creating fluid indoor-outdoor connections.</p>',
        category: 'Architecture',
        featured_image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&h=800&fit=crop',
        author: 'Editorial Team',
        created_at: new Date(Date.now() - 86400000).toISOString(),
        updated_at: new Date(Date.now() - 86400000).toISOString()
      },
      {
        title: 'Vignettes: Our Latest Travel Mini-Series Unpacked',
        slug: 'vignettes-travel-mini-series',
        excerpt: 'Explore hidden gems and architectural wonders through our new travel series capturing the essence of design around the world.',
        content: '<p>Our latest travel series takes you on a journey through some of the world\'s most inspiring architectural destinations. From minimalist Japanese homes to vibrant Mediterranean villas, we explore how different cultures approach design and living spaces.</p><p>Each episode focuses on a unique location, highlighting local craftsmanship, sustainable practices, and innovative design solutions.</p>',
        category: 'Travel',
        featured_image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&h=800&fit=crop',
        author: 'Editorial Team',
        created_at: new Date(Date.now() - 172800000).toISOString(),
        updated_at: new Date(Date.now() - 172800000).toISOString()
      },
      {
        title: 'Minimum Resources, Maximum Impact: Sustainable Fashion Mastery',
        slug: 'sustainable-fashion-mastery',
        excerpt: 'Reshaping the fashion industry, proving that limited resources can still result in a high impact, sustainable fashion line.',
        content: '<p>In an era of fast fashion and overconsumption, this story showcases how designers are creating beautiful, lasting pieces with minimal environmental impact. The focus is on quality over quantity, timeless design over trends.</p><p>Learn about innovative materials, ethical production methods, and the philosophy behind truly sustainable fashion.</p>',
        category: 'Art & Culture',
        featured_image: 'https://images.unsplash.com/photo-1558769132-cb1aea1f1d82?w=1200&h=800&fit=crop',
        author: 'Editorial Team',
        created_at: new Date(Date.now() - 259200000).toISOString(),
        updated_at: new Date(Date.now() - 259200000).toISOString()
      },
      {
        title: '1790 Mornington Peninsula Brews Excellence with In-Demand Coffee',
        slug: '1790-mornington-peninsula-coffee',
        excerpt: '1790 is setting high standards in the local coffee scene with its much sought-after, top-quality blends.',
        content: '<p>Nestled in the scenic Mornington Peninsula, 1790 has become a destination for coffee enthusiasts seeking exceptional brews in a beautifully designed space. The cafe\'s architecture complements its commitment to quality.</p><p>From the carefully sourced beans to the minimalist interior design, every detail has been considered to create an immersive coffee experience.</p>',
        category: 'Travel',
        featured_image: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=1200&h=800&fit=crop',
        author: 'Editorial Team',
        created_at: new Date(Date.now() - 345600000).toISOString(),
        updated_at: new Date(Date.now() - 345600000).toISOString()
      }
    ];
    
    for (const post of samplePosts) {
      await createPost(post);
    }
    
    console.log(`Created ${samplePosts.length} sample blog posts`);
  } catch (error) {
    console.error('Exception initializing sample posts:', error);
  }
};

// Subscribe to real-time blog post changes (not available with basic Neon/PostgreSQL)
// This function is kept for compatibility but does nothing
export const subscribeToBlogChanges = (callback) => {
  console.warn('Real-time subscriptions are not available with Neon/PostgreSQL');
  // Return a no-op cleanup function
  return () => {};
};
